import React, { useContext, useState } from "react";
import { useNavigate } from "react-router";
import { Form } from "react-final-form";
import { AuthContext, IntlContext } from "context";
import { useForgotPasswordDialog } from "context/ForgotPasswordDialog";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { TextField } from "components/Form/Field";
import Button from "ui/Button";
import { Button as LinkButton } from "components/ui/button";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { buildRoute, RESERVATION_STEPS } from "routes";

interface Props {
    serviceId: string | undefined;
}

const BookingLogin: React.FC<Props> = ({ serviceId }) => {
    const intl = useContext(IntlContext);
    const navigate = useNavigate();
    const { login } = useContext(AuthContext);
    const { actions: { openForgotPassword } } = useForgotPasswordDialog();
    const [inputIsPassword, setInputIsPassword] = useState<boolean>(true);

    type LoginFormData = {
        username: string;
        password: string;
    };

    const isRecord = (value: unknown): value is Record<string, unknown> =>
        typeof value === "object" && value !== null;

    const onSubmitLogin = async (data: LoginFormData) => {
        try {
            await login(data);
            navigate(buildRoute.providerReservation(String(serviceId), RESERVATION_STEPS.SET_CLIENT));
        } catch (err: unknown) {
            if (isRecord(err) && "response" in err && isRecord(err.response) && "data" in err.response) {
                const responseData = err.response.data;
                if (isRecord(responseData) && "data" in responseData) {
                    return formatErrorResponse(responseData as { data: unknown });
                }
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <h3 className="flex flex--align-items-center flex--justify-content-center padding-top--3">
                {intl.formatMessage("our_services.form.label.have_account")}
            </h3>
            <Form
                onSubmit={onSubmitLogin}
                render={({ handleSubmit }) => (
                    <form onSubmit={handleSubmit} className="login__form">
                        <TextField
                            inputProps={{
                                name: "username",
                                autoComplete: "login",
                                autoFocus: true,
                                required: true,
                            }}
                            labelProps={{
                                label: intl.formatMessage("our_services.form.label.email"),
                                requiredSymbole: "",
                            }}
                        />
                        <TextField
                            inputProps={{
                                name: "password",
                                autoComplete: "password",
                                required: true,
                                type: inputIsPassword ? "password" : "text",
                            }}
                            labelProps={{
                                label: intl.formatMessage("our_services.form.label.password"),
                                requiredSymbole: "",
                            }}
                            adornmentEnd={
                                <div onClick={() => setInputIsPassword(!inputIsPassword)} style={{ cursor: "pointer" }}>
                                    {inputIsPassword ? (
                                        <Icon icon={{ prefix: "fas", iconName: "eye-slash" }} />
                                    ) : (
                                        <Icon icon={{ prefix: "fas", iconName: "eye" }} />
                                    )}
                                </div>
                            }
                        />
                        <div className="login__bottom">
                            <LinkButton
                                type="button"
                                variant="link"
                                size="none"
                                className="login__forgot-password font-nunito text-sm font-bold text-indigo-600"
                                onClick={() => openForgotPassword()}
                            >
                                {intl.formatMessage("our_services.form.label.forgot_password")}
                            </LinkButton>
                        </div>
                        <div className="flex flex--align-items-center flex--justify-content-center margin-top--2 margin-bottom--2">
                            <Button type="submit" className="btn btn--primary">
                                {intl.formatMessage("our_services.form.action.login")}
                            </Button>
                        </div>
                    </form>
                )}
            />
        </React.Fragment>
    );
};

export default BookingLogin;
